'use client';

import React, { useEffect, useRef } from 'react';

/**
 * Liquid Glass & Displacement Canvas (Trend 2026)
 * Интерактивное преломление света, оптическая дисторсия и кинетическая 
 * векторная сетка, плавно реагирующая на движение курсора мыши и скролл.
 */
export const LiquidLensCanvas: React.FC<{ className?: string }> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    const mouse = {
      x: width * 0.5,
      y: height * 0.5,
      targetX: width * 0.5,
      targetY: height * 0.5,
      radius: 140,
      active: false,
    };

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.targetX = e.clientX - rect.left;
      mouse.targetY = e.clientY - rect.top;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
      mouse.targetX = width * 0.5;
      mouse.targetY = height * 0.5;
    };

    window.addEventListener('resize', handleResize);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    let time = 0;

    const render = () => {
      time += 0.015;
      mouse.x += (mouse.targetX - mouse.x) * 0.08;
      mouse.y += (mouse.targetY - mouse.y) * 0.08;

      ctx.clearRect(0, 0, width, height);

      // Рисуем благородную инженерную координатную сетку с преломлением
      const spacing = 36;
      const cols = Math.ceil(width / spacing);
      const rows = Math.ceil(height / spacing);

      ctx.lineWidth = 0.75;

      for (let i = 0; i <= cols; i++) {
        for (let j = 0; j <= rows; j++) {
          const x = i * spacing;
          const y = j * spacing;

          // Дистанция до курсора для линзового преломления (Displacement)
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Волновое смещение (Liquid Ripple)
          const wave = Math.sin(dist * 0.04 - time * 2) * 4;
          
          let displaceX = 0;
          let displaceY = 0;

          if (dist < mouse.radius) {
            const factor = Math.cos((dist / mouse.radius) * (Math.PI / 2));
            displaceX = (dx / dist) * factor * 14;
            displaceY = (dy / dist) * factor * 14;
          }

          const finalX = x + displaceX + Math.sin(time + j * 0.2) * 1.5;
          const finalY = y + displaceY + wave * 0.5;

          // Отрисовка тонких швейцарских перекрестий (Tactile Crosshairs)
          if ((i + j) % 2 === 0) {
            const alpha = Math.max(0.04, Math.min(0.25, 0.05 + (1 - dist / (mouse.radius * 2)) * 0.2));
            ctx.strokeStyle = `rgba(140, 109, 55, ${alpha})`;
            
            const crossSize = 2.5;
            ctx.beginPath();
            ctx.moveTo(finalX - crossSize, finalY);
            ctx.lineTo(finalX + crossSize, finalY);
            ctx.moveTo(finalX, finalY - crossSize);
            ctx.lineTo(finalX, finalY + crossSize);
            ctx.stroke();
          }
        }
      }

      // Мягкий оптический блик хрустальной линзы вокруг курсора
      const gradient = ctx.createRadialGradient(
        mouse.x, mouse.y, 0,
        mouse.x, mouse.y, mouse.radius * 1.3
      );
      gradient.addColorStop(0, 'rgba(214, 185, 126, 0.09)');
      gradient.addColorStop(0.5, 'rgba(140, 109, 55, 0.03)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(mouse.x, mouse.y, mouse.radius * 1.3, 0, Math.PI * 2);
      ctx.fill();

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
};
